import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import { ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react'
import {
	Table,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
} from 'components/ui/table'
import Spinner from 'components/Spinner'
import {
	initializeForScope,
	softDeleteDocument,
} from 'slices/documentLibrarySlice'
import {
	getCurrentVersion,
	matchesScope,
	scopeKey,
	type DocumentLibraryState,
	type LibraryDocument,
	type LibraryScopeArgs,
	type UserSummary,
} from '../types/documentLibrary.types'
import { getDocumentIcon } from '../utils/getDocumentIcon'
import { sourceLabel } from '../utils/sourceLabel'
import DocumentRow from './DocumentRow'
import DocumentLibraryEmptyState from './DocumentLibraryEmptyState'

type SortableColumn =
	| 'fileType'
	| 'fileName'
	| 'source'
	| 'owner'
	| 'uploaded'
	| 'docDate'
	| 'versionNumber'
type SortDirection = 'asc' | 'desc'

type DocumentLibraryListProps = LibraryScopeArgs & {
	onPreview?: (documentId: string) => void
	onShowVersions?: (documentId: string) => void
	onShare?: (documentId: string, versionId?: string) => void
	/**
	 * Fired when the user clicks a row outside the avatar / filename /
	 * action icons. Wired by the parent (project/portfolio/admin Documents
	 * tab) to open `DocumentEditSheet` (MYD-288).
	 */
	onRowClick?: (documentId: string) => void
	/** Empty-state CTA target. */
	onUpload?: () => void
	/**
	 * When true, render nothing (no table, no empty state) when this
	 * scope has no documents. Used by the Portfolio Documents page to
	 * hide the portfolio-scope empty state when project-scope docs
	 * cover the page already.
	 */
	hideWhenEmpty?: boolean
}

type ColumnDef = {
	id: SortableColumn | 'share' | 'download'
	label: string
	sortable: boolean
	width?: string
	align?: 'center'
}

const BASE_COLUMNS: ColumnDef[] = [
	{ id: 'fileType', label: '', sortable: true, width: 'w-14' },
	{ id: 'fileName', label: 'File Name', sortable: true },
	{ id: 'owner', label: 'Owner', sortable: true, width: 'w-40' },
	{ id: 'uploaded', label: 'Uploaded', sortable: true, width: 'w-24' },
	{ id: 'docDate', label: 'Doc Date', sortable: true, width: 'w-24' },
	{ id: 'versionNumber', label: 'Version', sortable: true, width: 'w-20', align: 'center' },
	{ id: 'share', label: 'Share', sortable: false, width: 'w-16', align: 'center' },
	{ id: 'download', label: 'Download', sortable: false, width: 'w-20', align: 'center' },
]

// In the my-library cross-scope view, prepend a Source column showing
// the doc's parent (Portfolio name / Project name / Location name /
// Global). Other scopes already know the parent from the page itself.
const COLUMNS_WITH_SOURCE: ColumnDef[] = [
	BASE_COLUMNS[0], // fileType
	BASE_COLUMNS[1], // fileName
	{ id: 'source', label: 'Source', sortable: true, width: 'w-40' },
	...BASE_COLUMNS.slice(2),
]

const ownerSortKey = (owner: LibraryDocument['owner']): string => {
	if (!owner || typeof owner === 'string') return ''
	const u = owner as UserSummary
	return [u.lastName, u.firstName, u.email].filter(Boolean).join(' ').toLowerCase()
}

const filenameSortKey = (doc: LibraryDocument): string => {
	const v = getCurrentVersion(doc)
	return (v?.originalFilename || doc.libraryName || '').toLowerCase()
}

const fileTypeSortKey = (doc: LibraryDocument): string => {
	const v = getCurrentVersion(doc)
	const name = v?.originalFilename || doc.libraryName || ''
	return getDocumentIcon(name).label.toLowerCase()
}


const getSortValue = (doc: LibraryDocument, column: SortableColumn): string | number => {
	const v = getCurrentVersion(doc)
	switch (column) {
		case 'fileType':
			return fileTypeSortKey(doc)
		case 'fileName':
			return filenameSortKey(doc)
		case 'source':
			return sourceLabel(doc).toLowerCase()
		case 'owner':
			return ownerSortKey(doc.owner)
		case 'uploaded':
			return v?.uploadedAt || doc.updatedAt || ''
		case 'docDate':
			return v?.fileTrueCreatedAt || ''
		case 'versionNumber':
			return v?.versionNumber ?? 0
		default:
			return ''
	}
}

const compare = (a: string | number, b: string | number): number => {
	if (typeof a === 'number' && typeof b === 'number') return a - b
	return String(a).localeCompare(String(b))
}

const noop = () => {}

// Rebuild a typed LibraryScopeArgs from props. Spread-destructuring
// doesn't preserve discriminated-union narrowing, so this `switch` is
// what gives the rest of the component a fully-narrowed value.
const buildScopeArgs = (props: DocumentLibraryListProps): LibraryScopeArgs => {
	switch (props.scope) {
		case 'project':
			return { scope: 'project', projectId: props.projectId }
		case 'portfolio':
			return { scope: 'portfolio', portfolioId: props.portfolioId }
		case 'location':
			return { scope: 'location', locationId: props.locationId }
		case 'global':
			return { scope: 'global' }
		case 'my-library':
			return { scope: 'my-library', userId: props.userId }
	}
}

/**
 * Pick the most recent timestamp on a document for sort + dedupe
 * purposes. Falls back through the version's uploadedAt → the doc's
 * updatedAt → epoch zero so the comparison always works.
 */
const docMostRecentTime = (doc: LibraryDocument): number => {
	const v = getCurrentVersion(doc)
	const candidate = v?.uploadedAt || doc.updatedAt
	if (!candidate) return 0
	const t = new Date(candidate).getTime()
	return Number.isFinite(t) ? t : 0
}

const DocumentLibraryList: React.FC<DocumentLibraryListProps> = props => {
	const {
		onPreview = noop,
		onShowVersions = noop,
		onShare = noop,
		onRowClick = noop,
		onUpload,
		hideWhenEmpty = false,
	} = props
	const scopeArgs = buildScopeArgs(props)
	const dispatch = useDispatch<any>()
	const scopeStringKey = scopeKey(scopeArgs)
	const { entities, ids, isLoading, initialized, error } = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) => ({
			entities: state.libraryDocuments.entities,
			ids: state.libraryDocuments.ids,
			isLoading: state.libraryDocuments.isLoading,
			initialized: state.libraryDocuments.initializedScopes.includes(scopeStringKey),
			error: state.libraryDocuments.error,
		}),
	)

	useEffect(() => {
		dispatch(initializeForScope(scopeArgs))
		// scopeStringKey is the stable identity for this scope; the
		// scopeArgs object itself changes identity on every render even
		// when nothing meaningful changed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scopeStringKey, dispatch])

	const docs = useMemo<LibraryDocument[]>(
		() => {
			if (scopeArgs.scope === 'my-library') {
				// Cross-scope view. matchesScope is `false` for this variant
				// (see types/documentLibrary.types.ts) — the slice replaces
				// `entities` entirely on each my-library fetch, so trusting
				// the cache here is correct.
				const all = ids
					.map(id => entities[id])
					.filter((d): d is LibraryDocument => !!d)
				// Dedupe by libraryName (case-insensitive). Keep the most
				// recently-uploaded instance as the representative row;
				// the other parents' rows still exist on the backend and
				// remain reachable from each parent's Documents tab.
				const byName = new Map<string, LibraryDocument>()
				for (const doc of all) {
					const key = doc.libraryName.toLowerCase()
					const existing = byName.get(key)
					if (!existing || docMostRecentTime(doc) > docMostRecentTime(existing)) {
						byName.set(key, doc)
					}
				}
				return Array.from(byName.values())
			}
			return ids
				.map(id => entities[id])
				.filter((d): d is LibraryDocument => matchesScope(d, scopeArgs))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[ids, entities, scopeStringKey],
	)

	const [orderBy, setOrderBy] = useState<SortableColumn | null>(null)
	const [order, setOrder] = useState<SortDirection>('desc')

	const sorted = useMemo(() => {
		if (!orderBy) return docs
		const sortedCopy = [...docs]
		sortedCopy.sort((a, b) => {
			const av = getSortValue(a, orderBy)
			const bv = getSortValue(b, orderBy)
			const c = compare(av, bv)
			return order === 'asc' ? c : -c
		})
		return sortedCopy
	}, [docs, orderBy, order])

	const handleSort = useCallback((column: SortableColumn) => {
		setOrderBy(prev => {
			if (prev === column) {
				setOrder(o => (o === 'asc' ? 'desc' : 'asc'))
				return prev
			}
			setOrder('asc')
			return column
		})
	}, [])

	const handleSoftDelete = useCallback(
		async (id: string) => {
			const ok = await toast.confirm({
				title: 'Delete document',
				message:
					'Delete this document? It will be hidden from the library; older versions remain in the audit trail.',
			})
			if (!ok) return
			dispatch(softDeleteDocument({ id }))
		},
		[dispatch],
	)

	const SortIcon = ({ column }: { column: SortableColumn }) => {
		if (orderBy !== column) {
			return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300 inline" />
		}
		return order === 'asc' ? (
			<ChevronUp className="ml-1 h-3 w-3 text-[#0080fe] inline" />
		) : (
			<ChevronDown className="ml-1 h-3 w-3 text-[#0080fe] inline" />
		)
	}

	if (isLoading && !initialized) {
		return (
			<div className="py-16 flex items-center justify-center">
				<Spinner />
			</div>
		)
	}

	if (error && !docs.length) {
		return (
			<div className="py-12 text-center text-sm text-red-600">{error}</div>
		)
	}

	if (!docs.length) {
		if (hideWhenEmpty) return null
		return <DocumentLibraryEmptyState onUpload={onUpload} />
	}

	const showSource = scopeArgs.scope === 'my-library'
	const columns = showSource ? COLUMNS_WITH_SOURCE : BASE_COLUMNS

	return (
		<div className="rounded-lg border border-gray-200">
			<Table className="table-fixed w-full">
				<TableHeader>
					<TableRow className="border-b-2 border-[#0080fe] hover:bg-transparent">
						{columns.map(col => (
							<TableHead
								key={col.id}
								className={[
									'text-gray-700 text-xs font-semibold whitespace-nowrap',
									col.sortable ? 'cursor-pointer select-none' : '',
									col.align === 'center' ? 'text-center' : '',
									col.width || '',
								].join(' ')}
								onClick={
									col.sortable
										? () => handleSort(col.id as SortableColumn)
										: undefined
								}
							>
								<span className="inline-flex items-center">
									{col.label}
									{col.sortable && (
										<SortIcon column={col.id as SortableColumn} />
									)}
								</span>
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{sorted.map(doc => (
						<DocumentRow
							key={doc._id}
							document={doc}
							onPreview={onPreview}
							onShowVersions={onShowVersions}
							onShare={onShare}
							onRowClick={onRowClick}
							onSoftDelete={handleSoftDelete}
							showSource={showSource}
						/>
					))}
				</TableBody>
			</Table>
		</div>
	)
}

export default DocumentLibraryList
